import { useState, useEffect, ReactNode, FunctionComponent, useRef } from 'react';
import './Header.scss';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ReactComponent as HomeIcon } from '@iba-team/portal-react-components/dist/svg/outline/home.svg';
import { ReactComponent as HelpIcon } from '@iba-team/portal-react-components/dist/svg/outline/question.svg';
import {ReactComponent as EmployeesIcon} from '@iba-team/portal-react-components/dist/svg/outline/users.svg';
import {ReactComponent as PlannerIcon} from '@iba-team/portal-react-components/dist/svg/outline/sort-by.svg';
import { ReactComponent as CalendarIcon } from '@iba-team/portal-react-components/dist/svg/outline/calendar.svg';
import { ReactComponent as ProfileIcon } from '@iba-team/portal-react-components/dist/svg/outline/profile.svg';
import { ReactComponent as SettingsIcon } from '@iba-team/portal-react-components/dist/svg/outline/settings.svg';
import { ReactComponent as LogoutIcon } from '@iba-team/portal-react-components/dist/svg/outline/logout.svg';
import { ReactComponent as ReportsIcon } from '@iba-team/portal-react-components/dist/svg/outline/bar-chart.svg';
import { ReactComponent as VacationOptionsIcon } from '@iba-team/portal-react-components/dist/svg/outline/options.svg';
import { ReactComponent as SurveyIcon } from '@iba-team/portal-react-components/dist/svg/outline/list.svg';
import { ReactComponent as ApplicationTemplateIcon } from '@iba-team/portal-react-components/dist/svg/outline/document.svg';
import Logo from '../../assets/logo-home.svg';
import {
  CALENDAR_ROUTE,
  HOME_ROUTE,
  REPORTS_ROUTE,
  HELP_ROUTE,
  PLANNER_ROUTE,
  SETTINGS_ROUTE, PROFILE_ROUTE, SURVEY_ROUTE, APPLICATION_TEMPLATES, ACTING_ROUTE
} from '../../utils/consts';
import { UserStore } from '../../store/User';
import { observer } from 'mobx-react';
import { NotificationI } from '../../types';
import useToken from '../../root/useToken';
import { Notifications } from '../../pages/home-page/Notification/Notifications';
import { AccessStore } from '../../store/Access';
import { HeaderLayout } from '@iba-team/portal-react-components/dist';
import { BREAKPOINT_HD, BREAKPOINT_LG } from '../../consts';
import {useLocation} from 'react-router';
import { useKeycloak } from '@react-keycloak/web';
import { useAnalyticsEventTracker } from '../../analytics/analytics';
import { SystemStore } from '../../store/System';
import { AuthStore } from '../../store/Auth';
import { useDeviceSizeThreshold } from '../../utils/hooks';
import { GlobalSearch } from './GlobalSearch';
import { io } from 'socket.io-client';
import apiConfig from '../../api/routes';
interface LinksI {
  id: number,
  title: string,
  name: string,
  link: string,
  icon: ReactNode,
  className?: string,
  ordinal: number,
  onChange?(): void,
  disabled?: boolean,
}

interface Props{
  limitedAccess: boolean
}

const Header:FunctionComponent<Props> = observer(({limitedAccess}) => {
  const { t } = useTranslation();
  const userStore = UserStore;
  const navigate = useNavigate();
  const {allItems,fetchMenuItems,fetchAllAccesses} = AccessStore;
  const {logout} = AuthStore;
  const [messages, setMessages] = useState<NotificationI[]>([]);
  const {token} = useToken();
  const [currentMenuItem, setCurrentMenuItem] = useState<LinksI|undefined>(undefined);
  const [headerLinks, setHeaderLinks] = useState<LinksI[]>([]);
  const isTablet = useDeviceSizeThreshold(BREAKPOINT_LG);
  const isHd = useDeviceSizeThreshold(BREAKPOINT_HD);
  const location = useLocation();
  const gaEventTracker = useAnalyticsEventTracker('Nav_elements');
  const {keycloak} = useKeycloak();
  const {systemState} = SystemStore;
  const {profile, fetchUserRoles } = UserStore;
  const redirectToLogin = (): void => {
    logout().then(() => {
      keycloak.logout();
      localStorage.removeItem('token');
    });
  };

  useEffect(()=>{
    fetchAllAccesses();
  },[]);

  useEffect(()=>{
    profile.id && fetchUserRoles();
  },[profile.id]);

  const socket = useRef<any>(null);

  useEffect(() => {
    socket.current = io(`${process.env.REACT_APP_NOTIFICATIONS_URL}`, {
      transports: ['websocket'],
      query: {
        Authorization: token
      }
    });
  }, []);


  socket.current?.on('unreadNotifications', (res: NotificationI[]) =>  {
    setMessages(res);
  });

  const deleteNotification = (id: number)=>{
    apiConfig.notificationService.deleteNotification(id);
  };
  const deleteNotificationsList = (ids: number[])=>{
    apiConfig.notificationService.deleteAllNotifications(ids);
  };

  socket.current?.on('newNotification', (res: NotificationI) =>  {
    setMessages(prev => {
      if (!prev.find(i => i.id === res.id)) {
        return [...prev, res];
      }
      return prev;
    });
  });

  useEffect(()=>{
    fetchMenuItems();
  },[window.location.pathname]);

  useEffect(() => {
    userStore.fetchUserProfile().then(()=> {
      userStore.fetchPortraitUrl();
    });
  }, [userStore, userStore.fetchUserProfile]);

  const modifyMenuItems = isTablet ? allItems.filter(el => el.name !== 'Planner' && el.name !== 'Survey') : allItems;

  useEffect(()=>{
    const logout = {
      id: -1,
      title: t('logOut'),
      name: t('logOut'),
      link: '',
      icon: <LogoutIcon />,
      disabled: false,
      onClick: redirectToLogin,
      ordinal: -1,
    };

    // eslint-disable-next-line array-callback-return
    setHeaderLinks(()=>([...[...modifyMenuItems.filter(i => i.visible).map(item=>{
      switch (item.name){
      case 'Survey':
        return  {
          id: item.id,
          title: t('takeSurvey'),
          name: item.name,
          className: `survey_header_element ${systemState.isNecessaryToBlock && 'survey_disabled'}`,
          link: SURVEY_ROUTE,
          icon: <SurveyIcon className="header__nav-links-home survey-icon" />,
          disabled: systemState.isNecessaryToBlock,
          ordinal: item.ordinal,
        };
      case 'Home':
        return  {
          id: item.id,
          title: t('home'),
          name: item.name,
          link: HOME_ROUTE,
          icon: <HomeIcon className="header__nav-links-home" />,
          disabled: systemState.isNecessaryToBlock,
          ordinal: item.ordinal,
        };
      case 'Calendar':
        return  {
          id: item.id,
          title: t('calendar'),
          name: item.name,
          onClick: () => gaEventTracker('calendar_page_link'),
          link: CALENDAR_ROUTE,
          icon: <CalendarIcon className="header__nav-links-calendar" />,
          disabled: systemState.isNecessaryToBlock,
          ordinal: item.ordinal,
        };
      case 'Reports':
        return {
          id: item.id,
          title: t('reports'),
          name: item.name,
          link: REPORTS_ROUTE,
          icon: <ReportsIcon className="header__nav-links-reports" />,
          disabled: systemState.isNecessaryToBlock,
          ordinal: item.ordinal,
        };
      case 'Planner':
        return {
          id: item.id,
          title: t('Planner'),
          name: item.name,
          link: PLANNER_ROUTE,
          icon: <PlannerIcon />,
          disabled: systemState.isNecessaryToBlock,
          ordinal: item.ordinal,
        };
      case 'Help':
        return {
          id: item.id,
          onClick: () => gaEventTracker('help_page_link'),
          title: t('help'),
          name: item.name,
          link: HELP_ROUTE,
          icon: <HelpIcon className="header__nav-links-help" />,
          disabled: systemState.isNecessaryToBlock,
          ordinal: item.ordinal,
        };
      case 'Employees':
        return {
          id: item.id,
          title: t('employees'),
          name: item.name,
          link: '',
          icon: <EmployeesIcon className="header__nav-links-employees" />,
          disabled: systemState.isNecessaryToBlock,
          ordinal: item.ordinal,
        };
      case 'Acting Settings':
        return {
          id: item.id,
          title: 'Acting Person',
          name: item.name,
          link: '',
          icon: <EmployeesIcon/>,
          disabled: systemState.isNecessaryToBlock,
          ordinal: item.ordinal,
          onClick: () => {
            navigate(ACTING_ROUTE);
            setCurrentMenuItem({id: item.id, title: item.name, name: item.name, ordinal: item.ordinal, icon: '', link: '', className: ''});
          },
        };
      case 'Application Templates':
        return {
          id: item.id,
          title: t('applicationTemplatesHeader'),
          name: item.name,
          link: '',
          icon: <ApplicationTemplateIcon />,
          disabled: !item.visible || isHd || systemState.isNecessaryToBlock,
          ordinal: item.ordinal,
          onClick: () => {
            navigate(APPLICATION_TEMPLATES);
            gaEventTracker('application_templates_link');
            setCurrentMenuItem({ id: item.id, title: item.name, name: item.name, ordinal: item.ordinal, icon: '', link: '', className: '' });
          }
        };
      case 'Profile':
        return {
          id: item.id,
          title: t('profile'),
          name: item.name,
          link: '',
          icon: <ProfileIcon />,
          disabled: isTablet || systemState.isNecessaryToBlock,
          ordinal: item.ordinal,
          onClick: () => {
            navigate(PROFILE_ROUTE);
            gaEventTracker('my_profile_link');
            setCurrentMenuItem({id: item.id, title: item.name, name: item.name, ordinal: item.ordinal, icon: '', link: '', className: ''});
          }
        };
      case 'Vacation Options':
        return {
          id: item.id,
          title: t('vacationOptions'),
          name: item.name,
          link: '',
          icon: <VacationOptionsIcon />,
          disabled: !item.visible || isTablet || systemState.isNecessaryToBlock,
          ordinal: item.ordinal,
          onClick:()=> {
            navigate(SETTINGS_ROUTE);
            gaEventTracker('vacation_options_link');
            setCurrentMenuItem({id: item.id, title: item.name, name: item.name, ordinal: item.ordinal, icon: '', link: '', className: ''});
          }
        };
      case 'Settings':
        return {
          id: item.id,
          title: t('settings'),
          name: item.name,
          link: '',
          icon: <SettingsIcon />,
          disabled: true,
          ordinal: item.ordinal,
        };
      }
    }) as unknown as LinksI[]], logout]));
  },[allItems, isTablet]);

  useEffect(()=>{
    headerLinks.filter(i => i.ordinal > 0).forEach(i => {
      const condition = location.pathname.length > 1 ?
        location.pathname.includes(i.link.substring(1,i.link.length-1), 1)
        :
        location.pathname.includes(i.link) ;
      condition && setCurrentMenuItem(i);
    });
    if(location.pathname.includes('profile')) {
      setCurrentMenuItem(undefined);
    }
  },[headerLinks, location.pathname]);


  const messagesToIds = messages.map(item=>(item.id));

  const sortByMessageTime = (a: NotificationI, b: NotificationI) => {
    return Date.parse(b.messageTime) - Date.parse(a.messageTime);
  };

  return (
    <HeaderLayout
      logo={{
        image: Logo,
        title: <div>
          Leave
          <br/>
          Management
        </div>
      }}
      className={'wrapperHead'}
      onLogoClick={()=> navigate(HOME_ROUTE)}
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      dropdownItems={headerLinks.filter(i => i.ordinal < 0)}
      renderSearch={() =>  profile.globalSearchView === 'NONE' ? null : <GlobalSearch/>}
      user={{ avatar: userStore.avatar, positionName: userStore.profile.positionName, name: userStore.profile.name }}
      urls={headerLinks.filter(i => i.ordinal > 0)}
      currentItem={currentMenuItem}
      setCurrentMenuItem={(value)=> {
        setCurrentMenuItem(value);
      }}
      messages={messages.sort((sortByMessageTime))}
      renderList={(msg: NotificationI, closeCallback: () => void) => <Notifications onClose={closeCallback}
        deleteNotif={deleteNotification}
        message={msg} />}
      deleteNotifications={deleteNotificationsList}
      messagesToId={() => messagesToIds}
      showNotifications={!limitedAccess}
    />
  );
});

export default Header;
